import { useEffect, useRef } from "react";
import { createRoot, type Root } from "react-dom/client";
import mapboxgl from "mapbox-gl";
import type { Event } from "../../reducers/apiTypes";
import { createPortal } from "react-dom";
import { MapEventPopup } from "./MapEventPopup";
import { eventTypeEmoji } from "../../utils/eventDisplay";

const Marker = ({
    map,
    events,
    selected = false,
    onSelectEvent,
}: {
    map: mapboxgl.Map | null;
    events: Event[];
    selected?: boolean;
    onSelectEvent?: (event: Event) => void;
}) => {
    const markerRef = useRef<mapboxgl.Marker | null>(null);
    const markerElementRef = useRef(document.createElement("div"));
    const popupRootRef = useRef<Root | null>(null);

    const anchor = events[0];
    const lat = anchor?.latitude;
    const lng = anchor?.longitude;
    const emoji = anchor ? eventTypeEmoji(anchor.event_type) : "📅";
    const eventCount = events.length;

    useEffect(() => {
        if (!map || !events.length || lat == null || lng == null) return;

        const popupEl = document.createElement("div");
        popupRootRef.current = createRoot(popupEl);
        popupRootRef.current.render(
            <MapEventPopup events={events} onSelectEvent={onSelectEvent} />
        );

        const popup = new mapboxgl.Popup({
            offset: 28,
            maxWidth: "none",
            className: "mapbox-event-popup-shell",
        }).setDOMContent(popupEl);

        const marker = new mapboxgl.Marker({
            element: markerElementRef.current,
        })
            .setPopup(popup)
            .setLngLat([lng, lat])
            .addTo(map);

        markerRef.current = marker;

        return () => {
            popupRootRef.current = null;
            marker.remove();
            markerRef.current = null;
        };
    }, [map, lat, lng, events, onSelectEvent]);

    if (!anchor || lat == null || lng == null) return null;

    const scale = selected ? 1.2 : 1;

    const handleDoubleClick = (event: Event) => {
        onSelectEvent?.(event);
    };

    return (
        <>
            {createPortal(
                <div
                    className="relative flex cursor-pointer items-center justify-center transition-transform"
                    style={{ transform: `scale(${scale})` }}
                    onDoubleClick={(e) => {
                        e.stopPropagation();
                        handleDoubleClick(anchor);
                    }}
                >
                    <span
                        className="text-3xl leading-none drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]"
                        aria-hidden
                    >
                        {emoji}
                    </span>
                    {eventCount > 1 && (
                        <span className="absolute -right-1 -top-1 flex min-w-4 items-center justify-center rounded-full bg-accent-orange px-1 text-[0.6rem] font-bold leading-4 text-white shadow-sm">
                            {eventCount}
                        </span>
                    )}
                </div>,
                markerElementRef.current
            )}
        </>
    );
};

export default Marker;
